import { useState, useEffect } from 'react'
import { Form, Button, Spinner, InputGroup } from 'react-bootstrap'
import Swal from 'sweetalert2'
import { FaPlus, FaTrash, FaSave } from 'react-icons/fa'
 
// Add some CSS for the custom styling.
// You can move this to a separate CSS file (e.g., IntendedLearners.css)
// and import it if you prefer.
const styles = `
  .intended-learners-container {
    background: linear-gradient(135deg, #a1c4fd 0%, #c2e9fb 100%);
    min-height: 100vh;
    padding: 2rem;
  }
 
  .learners-card {
    background: white;
    border-radius: 15px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
    padding: 2.5rem;
    max-width: 800px;
    margin: auto;
  }
 
  .learners-card h2 {
    color: #333;
    font-weight: 700;
  }
 
  .delete-icon {
    color: #dc3545;
    cursor: pointer;
    font-size: 1.2rem;
    transition: transform 0.2s ease-in-out;
  }
 
  .delete-icon:hover {
    transform: scale(1.2);
  }
 
  .form-control:focus {
    border-color: #8ec5fc;
    box-shadow: 0 0 0 0.25rem rgba(142, 197, 252, 0.4);
  }
 
  .add-button {
    font-weight: 600;
  }
 
  .save-button {
    background: linear-gradient(45deg, #4facfe 0%, #00f2fe 100%);
    border: none;
    font-weight: 600;
    padding: 10px 25px;
    transition: all 0.3s ease;
  }
 
  .save-button:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(79, 172, 254, 0.4);
  }
`
 
export default function IntendedLearners({ courseId }) {
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [learningObjectives, setLearningObjectives] = useState([''])
  const token = localStorage.getItem('jwttoken')
 
  // --- Fetch Course Details ---
  useEffect(() => {
    const fetchCourseDetails = async () => {
      setIsLoading(true)
      try {
        const response = await fetch(
          `https://kaasbackend.vetrinahealthcare.com/lms/courses/${courseId}/`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
 
        if (response.ok) {
          const data = await response.json()
          // Ensure course_key_points is an array and not empty
          const keyPoints =
            Array.isArray(data.course_key_points) &&
            data.course_key_points.length > 0
              ? data.course_key_points
              : [''] // Start with one empty input if none exist
          setLearningObjectives(keyPoints)
        } else {
          console.error('Failed to fetch course details.')
          Swal.fire({
            icon: 'error',
            title: 'Fetch Failed',
            text: 'Could not load course data.',
          })
        }
      } catch (error) {
        console.error('Error fetching course details:', error)
      } finally {
        setIsLoading(false)
      }
    }
 
    fetchCourseDetails()
  }, [courseId, token])
 
  // --- Handler Functions ---
  const handleObjectiveChange = (index, value) => {
    const newObjectives = [...learningObjectives]
    newObjectives[index] = value
    setLearningObjectives(newObjectives)
  }
 
  const handleAddObjective = () => {
    setLearningObjectives([...learningObjectives, ''])
  }
 
  const handleDeleteObjective = (index) => {
    // Prevent deleting the last remaining input field
    if (learningObjectives.length <= 1) {
      Swal.fire({
        icon: 'info',
        title: 'Cannot Delete',
        text: 'You must have at least one learning objective.',
      })
      return
    }
    const newObjectives = learningObjectives.filter((_, i) => i !== index)
    setLearningObjectives(newObjectives)
  }
 
  // --- API Update ---
  const updateCourseKeyPoints = async () => {
    setIsSaving(true)
    // Filter out any empty strings before sending to the backend
    const filteredObjectives = learningObjectives.filter((obj) => obj.trim() !== '')
 
    try {
      const response = await fetch(
        `https://kaasbackend.vetrinahealthcare.com/lms/courses/${courseId}/update-key-points/`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ course_key_points: filteredObjectives }),
        }
      )
 
      if (response.ok) {
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Key points updated successfully!',
          timer: 1500,
          showConfirmButton: false,
        })
      } else {
        const errorData = await response.json()
        Swal.fire({
          icon: 'error',
          title: 'Update Failed',
          text: errorData.error || 'Something went wrong.',
        })
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to update key points. Please try again later.',
      })
      console.error('Error updating key points:', error)
    } finally {
      setIsSaving(false)
    }
  }
 
  // --- Render Logic ---
  if (isLoading) {
    return (
      <div className='d-flex justify-content-center align-items-center vh-100'>
        <Spinner animation='border' variant='primary' role='status'>
          <span className='visually-hidden'>Loading...</span>
        </Spinner>
      </div>
    )
  }
 
  return (
    <>
      <style>{styles}</style>
      <div className='intended-learners-container'>
        <div className='learners-card'>
          <h2 className='mb-3'>Intended Learners</h2>
          <p className='text-muted mb-5'>
            The descriptions below will be displayed on your course landing page to help students decide if this course is right for them.
          </p>
 
          <div className='mb-5'>
            <h5>What will students learn in your course?</h5>
            <p className='text-muted'>
              Enter at least 4 learning objectives that learners can expect to
              achieve after completing your course.
            </p>
 
            {learningObjectives.map((objective, index) => (
              <InputGroup key={index} className='mb-3'>
                <Form.Control
                  type='text'
                  placeholder={`Example: ${
                    index % 2 === 0
                      ? 'Define key concepts in...'
                      : 'Apply a framework to...'
                  }`}
                  value={objective}
                  onChange={(e) => handleObjectiveChange(index, e.target.value)}
                />
                <Button
                  variant='outline-light'
                  onClick={() => handleDeleteObjective(index)}
                  className='d-flex align-items-center'
                  title='Delete objective'
                >
                  <FaTrash className='delete-icon' />
                </Button>
              </InputGroup>
            ))}
 
            <Button
              variant='link'
              className='text-primary p-0 add-button'
              onClick={handleAddObjective}
            >
              <FaPlus className='me-2' />
              Add more to your response
            </Button>
          </div>
 
          <Button
            variant='primary'
            className='save-button'
            onClick={updateCourseKeyPoints}
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <Spinner
                  as='span'
                  animation='border'
                  size='sm'
                  role='status'
                  aria-hidden='true'
                />
                <span className='ms-2'>Saving...</span>
              </>
            ) : (
              <>
                <FaSave className='me-2' />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>
    </>
  )
}
 